/**
 * CityMart — Global JS
 * Cart drawer, mobile nav, quantity controls, AJAX add-to-cart, variant selection,
 * collection filters, product recommendations
 */

(function () {
  'use strict';

  /* ---------- Helpers ---------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    var value = (cents / 100).toFixed(2);
    var formatString = format || '${{amount}}';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) {
        return 0;
      }
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var match = formatString.match(placeholderRegex);
    if (!match) {
      return value;
    }

    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  function parseJsonScript(el) {
    if (!el) {
      return null;
    }
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.error('Failed to parse JSON script', err);
      return null;
    }
  }

  function parseCartAddResponse(res) {
    return res.json().then(function (data) {
      if (!res.ok || (data && data.status && data.status >= 400)) {
        throw data;
      }
      return data;
    });
  }

  function metaPixelCurrency() {
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    return 'MAD';
  }

  function trackMetaAddToCart(item) {
    if (!item || typeof window.fbq !== 'function') {
      return;
    }
    var qty = Number(item.quantity) || 1;
    var unitPrice = (Number(item.price) || 0) / 100;
    window.fbq('track', 'AddToCart', {
      content_ids: [String(item.id)],
      content_name: item.product_title || item.title,
      content_type: 'product',
      contents: [{ id: String(item.id), quantity: qty, item_price: unitPrice }],
      value: unitPrice * qty,
      currency: metaPixelCurrency()
    });
  }

  function trackMetaInitiateCheckout(el) {
    if (typeof window.fbq !== 'function') {
      return;
    }
    var params = {
      content_type: 'product',
      currency: metaPixelCurrency()
    };
    var cents = el && el.getAttribute('data-cart-total-cents');
    if (cents) {
      params.value = Number(cents) / 100;
    }
    var countEl = document.querySelector('[data-cart-count]');
    if (countEl) {
      params.num_items = parseInt(countEl.textContent, 10) || 0;
    }
    window.fbq('track', 'InitiateCheckout', params);
  }

  /* ---------- Drawers (cart + mobile nav) ---------- */
  function openDrawer(id) {
    var drawer = document.getElementById(id);
    if (drawer) {
      drawer.hidden = false;
      document.body.style.overflow = 'hidden';
    }
  }

  function closeDrawer(drawer) {
    if (drawer) {
      drawer.hidden = true;
      document.body.style.overflow = '';
    }
  }

  function bindDrawerToggles(root) {
    (root || document).querySelectorAll('[data-drawer-toggle]').forEach(function (toggle) {
      if (toggle.dataset.bound === 'true') {
        return;
      }
      toggle.dataset.bound = 'true';
      toggle.addEventListener('click', function () {
        openDrawer(toggle.getAttribute('data-drawer-toggle'));
      });
    });

    (root || document).querySelectorAll('[data-drawer-close]').forEach(function (closeBtn) {
      if (closeBtn.dataset.bound === 'true') {
        return;
      }
      closeBtn.dataset.bound = 'true';
      closeBtn.addEventListener('click', function () {
        closeDrawer(closeBtn.closest('[data-drawer]'));
      });
    });
  }

  /* ---------- Cart drawer Section Rendering API ---------- */
  function updateCartCountFromDrawer(drawer) {
    var countEl = drawer && drawer.querySelector('[data-cart-count]');
    var count = countEl ? countEl.textContent.trim() : '0';
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
    });
  }

  function refreshCartDrawer() {
    return fetch('/?sections=cart-drawer')
      .then(function (res) {
        return res.json();
      })
      .then(function (sections) {
        var html = sections['cart-drawer'];
        if (!html) {
          return;
        }

        var existing = document.getElementById('cart-drawer');
        var wasOpen = existing && !existing.hidden;

        var temp = document.createElement('div');
        temp.innerHTML = html.trim();
        var newDrawer = temp.querySelector('#cart-drawer') || temp.firstElementChild;

        if (!newDrawer) {
          return;
        }

        if (wasOpen) {
          newDrawer.hidden = false;
        } else {
          newDrawer.hidden = true;
        }

        if (existing && existing.parentNode) {
          existing.parentNode.replaceChild(newDrawer, existing);
        } else {
          document.body.appendChild(newDrawer);
        }

        bindDrawerToggles(newDrawer);
        updateCartCountFromDrawer(newDrawer);
        return newDrawer;
      });
  }

  function updateCartLine(line, quantity) {
    return fetch(window.Shopify && window.Shopify.routes
      ? window.Shopify.routes.root + 'cart/change.js'
      : '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: quantity })
    })
      .then(function (res) {
        return res.json();
      })
      .then(function () {
        return refreshCartDrawer();
      })
      .catch(function (err) {
        console.error('Cart update failed', err);
      });
  }

  /* ---------- Variant selection ---------- */
  function initProductVariants() {
    var section = document.querySelector('[data-product-section]');
    if (!section) {
      return;
    }

    var variants = parseJsonScript(section.querySelector('[data-product-json]'));
    var strings = parseJsonScript(section.querySelector('[data-product-strings]')) || {};
    var moneyFormat = parseJsonScript(section.querySelector('[data-money-format]')) || '';
    var variantInput = section.querySelector('[data-variant-id-input]');
    var priceEl = section.querySelector('[data-product-price]');
    var addBtn = section.querySelector('[data-add-to-cart]');
    var optionGroups = section.querySelectorAll('.product-page__option');

    if (!variants || !variants.length || !optionGroups.length) {
      return;
    }

    function getSelectedOptions() {
      var selected = [];
      optionGroups.forEach(function (group) {
        var swatch = group.querySelector('.option-swatch.is-selected');
        selected.push(swatch ? swatch.getAttribute('data-option-value') : null);
      });
      return selected;
    }

    function findVariant(selectedOptions) {
      return variants.find(function (variant) {
        return selectedOptions.every(function (value, index) {
          return variant['option' + (index + 1)] === value;
        });
      });
    }

    function updatePrice(variant) {
      if (!priceEl || !variant) {
        return;
      }
      var priceHtml = '';
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceHtml =
          '<span class="product-page__price-compare" data-compare-price>' +
          formatMoney(variant.compare_at_price, moneyFormat) +
          '</span>' +
          '<span class="product-page__price-sale" data-sale-price>' +
          formatMoney(variant.price, moneyFormat) +
          '</span>';
      } else {
        priceHtml =
          '<span data-regular-price>' + formatMoney(variant.price, moneyFormat) + '</span>';
      }
      priceEl.innerHTML = priceHtml;
    }

    function updateAddButton(variant) {
      if (!addBtn) {
        return;
      }
      if (!variant || !variant.available) {
        addBtn.disabled = true;
        addBtn.classList.remove('button--primary');
        addBtn.classList.add('button--disabled');
        addBtn.textContent = strings.soldOut || 'Épuisé';
      } else {
        addBtn.disabled = false;
        addBtn.classList.add('button--primary');
        addBtn.classList.remove('button--disabled');
        addBtn.textContent = strings.addToCart || 'Ajouter au panier';
      }
    }

    function updateUrl(variant) {
      if (!variant || !window.history || !window.history.replaceState) {
        return;
      }
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    function onOptionChange() {
      var selected = getSelectedOptions();
      var variant = findVariant(selected);
      if (!variant) {
        updateAddButton(null);
        return;
      }
      if (variantInput) {
        variantInput.value = variant.id;
        variantInput.dispatchEvent(new Event('input', { bubbles: true }));
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      updatePrice(variant);
      updateAddButton(variant);
      updateUrl(variant);

      if (variant.featured_media) {
        var mediaId = String(variant.featured_media.id);
        section.querySelectorAll('.product-page__gallery-item').forEach(function (item) {
          item.classList.toggle('is-active', item.getAttribute('data-media-id') === mediaId);
        });
        section.querySelectorAll('[data-thumb]').forEach(function (thumb) {
          var isActive = thumb.getAttribute('data-media-id') === mediaId;
          thumb.classList.toggle('is-active', isActive);
          thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
      }

      section.dispatchEvent(
        new CustomEvent('variant:change', {
          bubbles: true,
          detail: { variant: variant }
        })
      );
    }

    optionGroups.forEach(function (optionGroup) {
      optionGroup.addEventListener('click', function (e) {
        var swatch = e.target.closest('[data-option-value]');
        if (!swatch) {
          return;
        }
        optionGroup.querySelectorAll('.option-swatch').forEach(function (s) {
          s.classList.remove('is-selected');
        });
        swatch.classList.add('is-selected');
        onOptionChange();
      });
    });

    section.addEventListener('citymart:select-variant', function (event) {
      var requestedId = event.detail && String(event.detail.variantId || '');
      var requestedVariant = variants.find(function (variant) {
        return String(variant.id) === requestedId;
      });

      if (!requestedVariant) {
        return;
      }

      var optionWasClicked = false;
      optionGroups.forEach(function (optionGroup, index) {
        var requestedValue = requestedVariant.options[index];
        var requestedSwatch = null;
        optionGroup.querySelectorAll('[data-option-value]').forEach(function (swatch) {
          if (swatch.getAttribute('data-option-value') === requestedValue) {
            requestedSwatch = swatch;
          }
        });

        if (requestedSwatch) {
          requestedSwatch.click();
          optionWasClicked = true;
        }
      });

      if (!optionWasClicked) {
        onOptionChange();
      }
    });
  }

  /* ---------- Camera package comparison ---------- */
  function bindCameraPackageLinks() {
    document.addEventListener('click', function (event) {
      var link = event.target.closest('[data-camera-package-link]');
      if (!link) {
        return;
      }

      var productSection = document.querySelector('[data-product-section]');
      if (!productSection) {
        return;
      }

      var targetUrl;
      try {
        targetUrl = new URL(link.href, window.location.href);
      } catch (err) {
        return;
      }

      var currentPath = window.location.pathname.replace(/\/$/, '');
      var targetPath = targetUrl.pathname.replace(/\/$/, '');
      if (currentPath !== targetPath) {
        return;
      }

      event.preventDefault();

      var variants = parseJsonScript(productSection.querySelector('[data-product-json]')) || [];
      var variantId = String(
        link.getAttribute('data-camera-variant-id') || targetUrl.searchParams.get('variant') || ''
      );
      var optionValue = String(link.getAttribute('data-camera-option-value') || '')
        .trim()
        .toLocaleLowerCase();

      if (!variantId && optionValue) {
        var matchedVariant = variants.find(function (variant) {
          return (variant.options || []).some(function (value) {
            return String(value).trim().toLocaleLowerCase() === optionValue;
          });
        });
        variantId = matchedVariant ? String(matchedVariant.id) : '';
      }

      if (variantId) {
        productSection.dispatchEvent(
          new CustomEvent('citymart:select-variant', {
            detail: { variantId: variantId }
          })
        );
      }

      var quantity = parseInt(link.getAttribute('data-camera-quantity') || '1', 10);
      var quantityInput = productSection.querySelector('[name="quantity"]');
      if (quantityInput && quantity > 0) {
        quantityInput.value = quantity;
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      productSection.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start'
      });

      var selectedSwatch = productSection.querySelector('.option-swatch.is-selected');
      if (selectedSwatch) {
        selectedSwatch.focus({ preventScroll: true });
      }
    });
  }

  /* ---------- Collection filters / sort ---------- */
  function initCollectionFilters() {
    var section = document.querySelector('[data-collection-section]');
    if (!section) {
      return;
    }

    var form = section.querySelector('[data-collection-filters-form]');
    var sectionId = section.getAttribute('data-section-id');
    if (!form || !sectionId) {
      return;
    }

    var debounceTimer = null;

    function buildUrl() {
      var params = new URLSearchParams(new FormData(form));
      // Remove empty values
      Array.from(params.keys()).forEach(function (key) {
        if (!params.get(key)) {
          params.delete(key);
        }
      });
      var base = form.getAttribute('action') || window.location.pathname;
      var qs = params.toString();
      return qs ? base + '?' + qs : base;
    }

    function fetchAndReplace(push) {
      var url = buildUrl();
      var fetchUrl = url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(sectionId);

      section.classList.add('is-loading');

      fetch(fetchUrl)
        .then(function (res) {
          return res.text();
        })
        .then(function (html) {
          var temp = document.createElement('div');
          temp.innerHTML = html;
          var newSection = temp.querySelector('[data-collection-section]');
          if (newSection && section.parentNode) {
            section.parentNode.replaceChild(newSection, section);
            section = newSection;
            form = section.querySelector('[data-collection-filters-form]');
            bindCollectionEvents();
            bindQuickAdd(section);
          }
          if (push) {
            window.history.pushState({ collectionFilters: true }, '', url);
          }
        })
        .catch(function (err) {
          console.error('Collection filter failed', err);
          // Fallback: full page navigation
          window.location.href = url;
        })
        .finally(function () {
          if (section) {
            section.classList.remove('is-loading');
          }
        });
    }

    function scheduleFetch() {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        fetchAndReplace(true);
      }, 250);
    }

    function bindCollectionEvents() {
      if (!form) {
        return;
      }
      form.addEventListener('change', function (e) {
        if (e.target.matches('[data-collection-sort], input[type="checkbox"], input[type="number"]')) {
          scheduleFetch();
        }
      });
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        fetchAndReplace(true);
      });

      var clearBtn = form.querySelector('[data-filter-clear]');
      if (clearBtn) {
        clearBtn.addEventListener('click', function (e) {
          e.preventDefault();
          window.location.href = form.getAttribute('action') || window.location.pathname;
        });
      }
    }

    bindCollectionEvents();

    window.addEventListener('popstate', function () {
      window.location.reload();
    });
  }

  /* ---------- Product recommendations ---------- */
  function loadRelatedSection(relatedEl) {
    if (!relatedEl || relatedEl.dataset.loaded === 'true') {
      return;
    }

    var url = relatedEl.getAttribute('data-url');
    if (!url) {
      return;
    }

    relatedEl.dataset.loaded = 'true';

    fetch(url)
      .then(function (res) {
        if (!res.ok) {
          throw new Error('Recommendations HTTP ' + res.status);
        }
        return res.text();
      })
      .then(function (text) {
        var temp = document.createElement('div');
        temp.innerHTML = text;

        var incoming = temp.querySelector('[data-related-products]');
        if (!incoming) {
          return;
        }

        var incomingGrid = incoming.querySelector('.related-products__grid, .product-grid');
        var hasCards = incomingGrid && incomingGrid.querySelector('.product-card');

        // Only replace when the API returned real recommendation cards
        // (avoid wiping the collection fallback with an empty complementary result)
        if (hasCards) {
          relatedEl.parentNode.replaceChild(incoming, relatedEl);
          bindQuickAdd(incoming);
        }
      })
      .catch(function (err) {
        console.error('Recommendations failed', err);
        relatedEl.dataset.loaded = 'false';
      });
  }

  function initRelatedProducts() {
    document.querySelectorAll('[data-related-products]').forEach(function (el) {
      // Prefer loading when visible; fall back to immediate fetch
      if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                observer.unobserve(entry.target);
                loadRelatedSection(entry.target);
              }
            });
          },
          { rootMargin: '200px 0px' }
        );
        observer.observe(el);
      } else {
        loadRelatedSection(el);
      }
    });
  }

  /* ---------- Quick add ---------- */
  function bindQuickAdd(root) {
    (root || document).querySelectorAll('[data-quick-add]').forEach(function (btn) {
      if (btn.dataset.bound === 'true') {
        return;
      }
      btn.dataset.bound = 'true';
      btn.addEventListener('click', function () {
        var variantId = btn.getAttribute('data-variant-id');
        if (!variantId) {
          return;
        }
        btn.disabled = true;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity: 1 })
        })
          .then(parseCartAddResponse)
          .then(function (item) {
            trackMetaAddToCart(item);
            return refreshCartDrawer();
          })
          .then(function () {
            openDrawer('cart-drawer');
          })
          .catch(function (err) {
            console.error('Quick add failed', err);
          })
          .finally(function () {
            btn.disabled = false;
          });
      });
    });
  }

  /* ---------- Product form AJAX ---------- */
  function bindProductForms() {
    document.querySelectorAll('[data-product-form]').forEach(function (form) {
      if (form.dataset.bound === 'true') {
        return;
      }
      form.dataset.bound = 'true';
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var formData = new FormData(form);
        var submitBtn = form.querySelector('[data-add-to-cart]');
        if (submitBtn) {
          submitBtn.disabled = true;
        }

        fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json' }
        })
          .then(parseCartAddResponse)
          .then(function (item) {
            trackMetaAddToCart(item);
            return refreshCartDrawer();
          })
          .then(function () {
            openDrawer('cart-drawer');
          })
          .catch(function (err) {
            console.error('Add to cart failed', err);
          })
          .finally(function () {
            if (submitBtn && !submitBtn.classList.contains('button--disabled')) {
              submitBtn.disabled = false;
            }
          });
      });
    });
  }

  /* ---------- Quantity selectors ---------- */
  function bindQuantitySelectors() {
    document.querySelectorAll('[data-quantity-increase]').forEach(function (btn) {
      if (btn.dataset.bound === 'true') {
        return;
      }
      btn.dataset.bound = 'true';
      btn.addEventListener('click', function () {
        var input = btn.parentElement.querySelector('[data-quantity-input]');
        if (input) {
          input.value = parseInt(input.value || 1, 10) + 1;
        }
      });
    });
    document.querySelectorAll('[data-quantity-decrease]').forEach(function (btn) {
      if (btn.dataset.bound === 'true') {
        return;
      }
      btn.dataset.bound = 'true';
      btn.addEventListener('click', function () {
        var input = btn.parentElement.querySelector('[data-quantity-input]');
        if (input) {
          var val = parseInt(input.value || 1, 10) - 1;
          input.value = val < 1 ? 1 : val;
        }
      });
    });
  }

  /* ---------- Gallery thumbnails ---------- */
  function bindGalleryThumbs() {
    document.querySelectorAll('[data-thumb]').forEach(function (thumb) {
      if (thumb.dataset.bound === 'true') {
        return;
      }
      thumb.dataset.bound = 'true';
      thumb.addEventListener('click', function () {
        var mediaId = thumb.getAttribute('data-media-id');
        var gallery = thumb.closest('[data-product-gallery]') || document;
        gallery.querySelectorAll('.product-page__gallery-item').forEach(function (item) {
          item.classList.toggle('is-active', item.getAttribute('data-media-id') === mediaId);
        });
        gallery.querySelectorAll('[data-thumb]').forEach(function (t) {
          var isActive = t === thumb;
          t.classList.toggle('is-active', isActive);
          t.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
      });
    });
  }

  /* ---------- Cart line qty / remove (event delegation) ---------- */
  document.addEventListener('click', function (e) {
    var checkoutEl = e.target.closest('[data-meta-checkout]');
    if (checkoutEl) {
      trackMetaInitiateCheckout(checkoutEl);
    }

    var incBtn = e.target.closest('[data-cart-quantity-increase]');
    var decBtn = e.target.closest('[data-cart-quantity-decrease]');
    var removeBtn = e.target.closest('[data-cart-remove]');

    if (incBtn || decBtn) {
      var item = (incBtn || decBtn).closest('[data-cart-item]');
      if (!item) {
        return;
      }
      var line = item.getAttribute('data-line');
      var qtyEl = item.querySelector('[data-cart-quantity]');
      var qty = parseInt(qtyEl.textContent, 10);
      qty = incBtn ? qty + 1 : Math.max(0, qty - 1);
      updateCartLine(line, qty);
    }

    if (removeBtn) {
      var removeItem = removeBtn.closest('[data-cart-item]');
      if (!removeItem) {
        return;
      }
      updateCartLine(removeItem.getAttribute('data-line'), 0);
    }
  });

  /* ---------- Trust popup (first visit) ---------- */
  function initTrustPopup() {
    var popup = document.querySelector('[data-trust-popup]');
    if (!popup) {
      return;
    }

    var storageKey = popup.getAttribute('data-storage-key') || 'citymart-trust-popup-seen';
    var delay = parseInt(popup.getAttribute('data-delay') || '800', 10);
    var designMode = window.Shopify && window.Shopify.designMode;

    function openPopup() {
      popup.hidden = false;
      document.body.style.overflow = 'hidden';
      var closeBtn = popup.querySelector('.trust-popup__close');
      if (closeBtn) {
        closeBtn.focus();
      }
    }

    function closePopup(markSeen) {
      popup.hidden = true;
      document.body.style.overflow = '';
      if (markSeen !== false) {
        try {
          window.localStorage.setItem(storageKey, '1');
        } catch (err) {
          /* private mode / blocked storage */
        }
      }
    }

    popup.querySelectorAll('[data-trust-popup-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        closePopup(true);
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !popup.hidden) {
        closePopup(true);
      }
    });

    if (designMode) {
      // Always available in theme editor; open once so merchants can preview
      openPopup();
      return;
    }

    var seen = false;
    try {
      seen = window.localStorage.getItem(storageKey) === '1';
    } catch (err) {
      seen = false;
    }

    if (seen) {
      return;
    }

    window.setTimeout(openPopup, isNaN(delay) ? 800 : delay);
  }

  document.addEventListener('DOMContentLoaded', function () {
    bindDrawerToggles();
    bindQuantitySelectors();
    bindProductForms();
    bindQuickAdd();
    bindGalleryThumbs();
    bindCameraPackageLinks();
    initProductVariants();
    initCollectionFilters();
    initRelatedProducts();
    initTrustPopup();
  });
})();
